import { useEffect, useRef } from 'react'

import { KEYBOARD_KEYS } from 'shared/ui/types'
import { isElementVisible } from 'shared/ui/utils/layout'

const focusableElementsSelector = `
  a[href],
  area[href],
  input:not([disabled]):not([type="hidden"]),
  select:not([disabled]),
  textarea:not([disabled]),
  button:not([disabled]),
  iframe,
  object,
  embed,
  [contenteditable],
  [tabindex]:not([tabindex="-1"])
`

interface UseFocusTrapOptions {
  container: React.RefObject<HTMLElement | null> | HTMLElement | null
  initialFocusAnchor?: HTMLElement | null
  active?: boolean
  /** When true, do not move focus into the container on activate. Tab from previouslyFocusedElement will move focus in. */
  skipInitialFocus?: boolean
  previouslyFocusedElement?: React.RefObject<HTMLElement | null>
}

export function useFocusTrap({
  container,
  active,
  initialFocusAnchor,
  previouslyFocusedElement,
  skipInitialFocus = false
}: UseFocusTrapOptions) {
  // 1. Store the previously focused element
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const focusContainer =
      container && 'current' in container ? container.current : container

    // 2. Check if container exists and focus trap is active
    if (!focusContainer || !active) return

    // 3. Select default or defined element to return focus to
    if (!returnFocusRef.current) {
      returnFocusRef.current =
        previouslyFocusedElement?.current ??
        (document.activeElement as HTMLElement)
    }

    // 4. Get all focusable elements within the container
    const getFocusableElements = (root: HTMLElement | null) => {
      if (!root) return []

      return Array.from(
        root.querySelectorAll<HTMLElement>(focusableElementsSelector)
      ).filter(
        element =>
          !element.hasAttribute('disabled') &&
          element.getAttribute('aria-hidden') !== 'true' &&
          !(element instanceof HTMLInputElement && element.type === 'hidden') &&
          isElementVisible(element)
      )
    }

    // 5. Optional initial focus (skip when skipInitialFocus – e.g. popover: Tab moves focus in)
    if (!skipInitialFocus) {
      const focus = async () => {
        let attempts = 0
        let focused = false

        while (!focused && attempts < 10) {
          await new Promise<void>(resolve =>
            requestAnimationFrame(() => resolve())
          )
          const elements = getFocusableElements(focusContainer)
          const first = elements[0]
          const firstInputElement = elements.find(
            element => element instanceof HTMLInputElement
          )
          const initialFocusElement =
            initialFocusAnchor ?? firstInputElement ?? first

          if (!initialFocusElement) {
            attempts++
            await new Promise(resolve => setTimeout(resolve, 100))
            continue
          }

          initialFocusElement.focus()

          if (document.activeElement === initialFocusElement) {
            focused = true
            break
          }

          attempts++
          await new Promise(resolve => setTimeout(resolve, 100))
        }

        return focused
      }

      void focus()
    }

    const anchorElement = previouslyFocusedElement?.current ?? null
    const handlePreviouslyFocusedElementKeyDown = (e: KeyboardEvent) => {
      if (e.key !== KEYBOARD_KEYS.TAB) return

      const elements = getFocusableElements(focusContainer)

      if (elements.length === 0) return

      const first = elements[0]
      const last = elements[elements.length - 1]

      e.preventDefault()
      if (e.shiftKey) {
        last.focus()
      } else {
        first.focus()
      }
    }

    if (active)
      anchorElement?.addEventListener(
        'keydown',
        handlePreviouslyFocusedElementKeyDown
      )

    // 7. Handle focus trap - cycle focus within the container
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== KEYBOARD_KEYS.TAB) return

      const elements = getFocusableElements(focusContainer)

      if (elements.length === 0) return

      const firstEl = elements[0]
      const lastEl = elements[elements.length - 1]

      if (document.activeElement === anchorElement) {
        e.preventDefault()
        firstEl.focus()
      }

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus()
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }

    focusContainer.addEventListener('keydown', handleKeyDown)

    // 8. Cleanup - remove event listener, restore focus on previously focused element
    return () => {
      focusContainer.removeEventListener('keydown', handleKeyDown)
      const toRestore = returnFocusRef.current

      toRestore?.focus()
      returnFocusRef.current = null
      anchorElement?.removeEventListener(
        'keydown',
        handlePreviouslyFocusedElementKeyDown
      )
    }
  }, [
    container,
    active,
    initialFocusAnchor,
    skipInitialFocus,
    previouslyFocusedElement
  ])
}
